import { useEffect, useState } from "react";
import "./EditOrder.css";
import { useDispatch, useSelector } from "react-redux";
import { OrdersDispatch, OrdersState } from "../../Redux/OrdersContext";
import { useForm } from "react-hook-form";
import OrderModel from "../../Models/OrderModel";
import { updateOrder, deleteOrder, setInputFocusTyping } from "../../Redux/slicers/ordersSlicer";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";


function EditOrder(): JSX.Element {
    let pickedOrder = useSelector((state: OrdersState) => state.orders.pickedOrder);
    const dispatch: OrdersDispatch = useDispatch();
    const currentDate = new Date();
    const [totalPrice, setTotalPrice] = useState("");
    const [isPriceFocused, setIsPriceFocused] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState("");
    const [pickupDate, setPickupDate] = useState(currentDate);
    const [orderDate, setOrderDate] = useState(currentDate);
    const [isTransferChecked, setIsTransferChecked] = useState(false);
    const [activeStatus, setActiveStatus] = useState<"is_paid" | "is_ready_for_pickup" | "is_cancelled" | null>(null);
    const [confirmModal, setConfirmModal] = useState<{ message: string; onConfirm: () => void } | null>(null);
    const { register, handleSubmit, formState, setValue, setError } = useForm<OrderModel>();

    const selectStatus = (status: "is_paid" | "is_ready_for_pickup" | "is_cancelled" | null) => {
        const next = activeStatus === status ? null : status;
        setActiveStatus(next);
        setValue("is_paid", next === "is_paid");
        setValue("is_ready_for_pickup", next === "is_ready_for_pickup");
        setValue("is_cancelled", next === "is_cancelled");
    };

    function handleTotalPriceChange(event: React.ChangeEvent<HTMLInputElement>) {
        const value = event.target.value.replace(/[^\d.]/g, "");
        setTotalPrice(value);
        if (formState.errors.total_price) {
            formState.errors.total_price.message = "";
        }
    }

    function handlePhoneNumberChange(event: React.ChangeEvent<HTMLInputElement>) {
        const value = event.target.value.replace(/[^\d.]/g, "");
        setPhoneNumber(value);
        if (formState.errors.total_price) {
            formState.errors.total_price.message = "";
        }
    }

    function handleTransferChange() {
        setIsTransferChecked(!isTransferChecked);
    }

    // Disable input fields in date picker and handle click outside of the form
    useEffect(() => {
        document.querySelectorAll(".react-datepicker__input-container input").forEach((input) => { (input as HTMLInputElement).readOnly = true });
        const backgroundHandler = document.getElementById("EditOrder");
        const handleClick = (e: MouseEvent) => {
            if (e.target === backgroundHandler) {
                closeEditOrder();
            }
        };
        const escapeKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                closeEditOrder();
            }
        };
        if (backgroundHandler) {
            backgroundHandler.addEventListener("click", handleClick);
        }
        window.addEventListener("keydown", escapeKey);
        // Cleanup event listener on component unmount
        return () => {
            if (backgroundHandler) {
                backgroundHandler.removeEventListener("click", handleClick);
            }
        };
    });



    useEffect(() => {
        if (pickedOrder) {
            setValue("order_number", pickedOrder.order_number);
            setValue("pickup_date", pickedOrder.pickup_date);
            setPickupDate(new Date(pickedOrder.pickup_date));
            setValue("pickup_branch", pickedOrder.pickup_branch);
            setValue("customer_name", pickedOrder.customer_name);
            setPhoneNumber(pickedOrder.customer_phone.replace("+972", "0"));
            setValue("customer_email", pickedOrder.customer_email);
            setValue("description", pickedOrder.description);
            setValue("total_price", pickedOrder.total_price);
            setTotalPrice(String(Math.round(pickedOrder.total_price)));
            setValue("order_date", pickedOrder.order_date);
            setOrderDate(new Date(pickedOrder.order_date));
            setValue("is_paid", pickedOrder.is_paid);
            setValue("transfer", pickedOrder.transfer);
            setIsTransferChecked(pickedOrder.transfer.valueOf());
            setValue("transfer_branch", pickedOrder.transfer_branch);
            setValue("is_transferred", pickedOrder.is_transferred);
            setValue("is_ready_for_pickup", pickedOrder.is_ready_for_pickup);
            setValue("is_cancelled", pickedOrder.is_cancelled);
            setActiveStatus(pickedOrder.is_cancelled ? "is_cancelled" : pickedOrder.is_paid ? "is_paid" : pickedOrder.is_ready_for_pickup ? "is_ready_for_pickup" : null);
        }
    }, [pickedOrder, setValue]);

    function closeEditOrder() {
        const backgroundHandler = document.getElementById("EditOrder");
        if (backgroundHandler) {
            backgroundHandler.classList.remove("visible");
        }
        dispatch(setInputFocusTyping(true));
    }

    function wipeOrder() {
        setConfirmModal({
            message: "האם אתה בטוח שברצונך למחוק הזמנה זו?",
            onConfirm: async () => {
                try {
                    dispatch(deleteOrder(pickedOrder.id));
                    closeEditOrder();
                } catch (err) {
                    alert(err);
                }
            }
        });
    }

    async function send(order: OrderModel) {
        order.total_price = parseFloat(totalPrice);
        order.pickup_date = pickupDate.toISOString().split("T")[0];
        order.order_date = orderDate.toISOString().split("T")[0];
        order.customer_phone = phoneNumber;
        if (isTransferChecked && order.is_ready_for_pickup && !order.is_transferred) {
            setError("is_ready_for_pickup", { "message": "הזמנה עם העברה לא יכולה להיות מוכנה לאיסוף" });
            return;
        }
        try {
            pickedOrder = {
                ...order,
                id: pickedOrder.id,
                is_paid: activeStatus === "is_paid",
                is_ready_for_pickup: activeStatus === "is_ready_for_pickup",
                is_cancelled: activeStatus === "is_cancelled",
            }
            const updateRequest = dispatch(updateOrder(pickedOrder)).unwrap();
            closeEditOrder();
            await updateRequest;
        } catch (err) {
            alert(err);
        }
    }


    return (
        <div className="EditOrder" id="EditOrder">
            <div className="EO-panel">

                {/* ── Header ── */}
                <div className="EO-header">
                    <button type="button" className="EO-close" onClick={closeEditOrder}>✕</button>
                    <div className="EO-header-center">
                        <span className="EO-label">עריכת הזמנה</span>
                        <span className="EO-order-num">{pickedOrder?.order_number}</span>
                        <div className="EO-header-sub">
                            <span>{pickedOrder?.customer_name}</span>
                            <span className="EO-sep">·</span>
                            <span>₪{Number(pickedOrder?.total_price).toLocaleString('he-IL', { maximumFractionDigits: 0 })}</span>
                        </div>
                    </div>
                </div>

                {/* ── Scrollable body ── */}
                <div className="EO-body">
                    <form>
                        {formState.errors.is_ready_for_pickup && (
                            <div className="EO-alert">{formState.errors.is_ready_for_pickup.message}</div>
                        )}

                        {/* Order Details */}
                        <section className="EO-section">
                            <p className="EO-section-title">פרטי הזמנה</p>
                            <div className="EO-grid cols-3">
                                <div className="EO-field">
                                    <label>מספר הזמנה</label>
                                    <input type="text" {...register("order_number", { required: true })} />
                                </div>
                                <div className="EO-field">
                                    <label>תאריך איסוף</label>
                                    <DatePicker
                                        selected={pickupDate}
                                        onChange={(date) => setPickupDate(date)}
                                        dateFormat="dd/MM"
                                    />
                                    <input type="hidden" {...register("pickup_date")} value={pickupDate.toISOString().split("T")[0]} />
                                </div>
                                <div className="EO-field">
                                    <label>תאריך הזמנה</label>
                                    <DatePicker
                                        selected={orderDate}
                                        onChange={(date) => setOrderDate(date)}
                                        dateFormat="dd/MM"
                                    />
                                    <input type="hidden" {...register("order_date")} value={orderDate.toISOString().split("T")[0]} />
                                </div>
                            </div>
                            <div className="EO-grid cols-1 mt-16">
                                <div className="EO-field">
                                    <label>תיאור</label>
                                    <input type="text" {...register("description", { required: true })} />
                                </div>
                            </div>
                            <div className="EO-grid cols-1 mt-16">
                                <div className="EO-field">
                                    <label>סכום כולל ₪</label>
                                    <input type="text"
                                        value={isPriceFocused ? totalPrice : (totalPrice ? Number(totalPrice).toLocaleString('he-IL', { maximumFractionDigits: 0 }) : "")}
                                        onFocus={() => setIsPriceFocused(true)}
                                        onBlur={() => setIsPriceFocused(false)}
                                        {...register("total_price", {
                                            required: true,
                                            onChange: (e) => handleTotalPriceChange(e),
                                        })} />
                                </div>
                            </div>
                        </section>

                        {/* Customer */}
                        <section className="EO-section">
                            <p className="EO-section-title">פרטי לקוח</p>
                            <div className="EO-grid cols-3">
                                <div className="EO-field">
                                    <label>שם לקוח</label>
                                    <input type="text" {...register("customer_name", { required: true })} />
                                </div>
                                <div className="EO-field">
                                    <label>טלפון</label>
                                    <input type="text" value={phoneNumber} {...register("customer_phone", {
                                        onChange: (e) => handlePhoneNumberChange(e),
                                    })} />
                                </div>
                                <div className="EO-field">
                                    <label>אימייל</label>
                                    <input type="text" {...register("customer_email", { required: true })} />
                                </div>
                            </div>
                        </section>

                        {/* Logistics */}
                        <section className="EO-section">
                            <p className="EO-section-title">לוגיסטיקה</p>
                            <div className="EO-grid cols-2">
                                <div className="EO-field">
                                    <label>סניף איסוף</label>
                                    <select {...register("pickup_branch", { required: true })}>
                                        <option value="1">ביג</option>
                                        <option value="2">דיסק קלאב</option>
                                        <option value="3">אייסמול</option>
                                        <option value="4">רד</option>
                                        <option value="5">טיילת</option>
                                        <option value="6">משלוח לבית הלקוח באילת</option>
                                        <option value="7">משלוח לבית הלקוח</option>
                                        <option value="8">מחסן ראשי</option>
                                    </select>
                                </div>
                                <div className="EO-field EO-field-chip">
                                    <label className="EO-chip">
                                        <input type="checkbox" className="chip-check" onClick={() => handleTransferChange()} {...register("transfer")} />
                                        <span className="chip-dot"></span>
                                        <span>העברה מסניף אחר</span>
                                    </label>
                                </div>
                            </div>

                            {isTransferChecked && (
                                <div className="EO-grid cols-2 mt-16">
                                    <div className="EO-field">
                                        <label>סניף שולח</label>
                                        <select {...register("transfer_branch")}>
                                            <option value="1">ביג</option>
                                            <option value="2">דיסק קלאב</option>
                                            <option value="3">אייסמול</option>
                                            <option value="4">רד</option>
                                            <option value="5">טיילת</option>
                                            <option value="6">משלוח לבית הלקוח באילת</option>
                                            <option value="7">משלוח לבית הלקוח</option>
                                            <option value="8">מחסן ראשי</option>
                                        </select>
                                    </div>
                                    <div className="EO-field EO-field-chip">
                                        <label className="EO-chip">
                                            <input type="checkbox" className="chip-check" {...register("is_transferred")} />
                                            <span className="chip-dot"></span>
                                            <span>הועבר</span>
                                        </label>
                                    </div>
                                </div>
                            )}
                        </section>

                        {/* Status */}
                        <section className="EO-section">
                            <p className="EO-section-title">סטטוס</p>
                            <div className="EO-chips">
                                <label className={"EO-chip" + (activeStatus === "is_paid" ? " EO-chip--active" : "")} onClick={(e) => { e.preventDefault(); selectStatus("is_paid"); }}>
                                    <input type="checkbox" className="chip-check" checked={activeStatus === "is_paid"} onChange={() => { }} />
                                    <span className="chip-dot"></span>
                                    <span>שולם</span>
                                </label>
                                <label className={"EO-chip" + (activeStatus === "is_ready_for_pickup" ? " EO-chip--active" : "")} onClick={(e) => { e.preventDefault(); selectStatus("is_ready_for_pickup"); }}>
                                    <input type="checkbox" className="chip-check" checked={activeStatus === "is_ready_for_pickup"} onChange={() => { }} />
                                    <span className="chip-dot"></span>
                                    <span>מוכן לאיסוף</span>
                                </label>
                                <label className={"EO-chip EO-chip--warn" + (activeStatus === "is_cancelled" ? " EO-chip--active" : "")} onClick={(e) => { e.preventDefault(); selectStatus("is_cancelled"); }}>
                                    <input type="checkbox" className="chip-check" checked={activeStatus === "is_cancelled"} onChange={() => { }} />
                                    <span className="chip-dot"></span>
                                    <span>בוטלה</span>
                                </label>
                            </div>
                        </section>
                    </form>
                </div>

                {/* ── Footer ── */}
                <div className="EO-footer">
                    <button type="button" className="EO-btn-save" onClick={handleSubmit((data) => send(data))}>
                        שמור שינויים
                    </button>
                    <div className="EO-footer-right">
                        <button type="button" className="EO-btn-delete" onClick={wipeOrder}>מחק</button>
                    </div>
                </div>

            </div>

            {/* ── Confirm Modal ── */}
            {confirmModal && (
                <div className="EO-confirm-backdrop" onClick={() => setConfirmModal(null)}>
                    <div className="EO-confirm-box" onClick={e => e.stopPropagation()}>
                        <p className="EO-confirm-msg">{confirmModal.message}</p>
                        <div className="EO-confirm-actions">
                            <button className="EO-confirm-yes" onClick={() => { confirmModal.onConfirm(); setConfirmModal(null); }}>כן, המשך</button>
                            <button className="EO-confirm-no" onClick={() => setConfirmModal(null)}>ביטול</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default EditOrder;